// src/runtime/ProductRuntimes/index.ts
// ============================================
// 产品 Runtime 入口 - 汇总所有产品 Runtime 子类
//
// 每个产品 Runtime 通过继承 BaseProductRuntime 实现，
// 只需重写 buildCatalog() 返回该产品的静态元信息，
// 其余（Vault 增强、launch、状态管理）由基类统一处理。
//
// 新增产品 Runtime 步骤：
//   1. 在 RuntimeName 枚举中新增条目
//   2. 在此文件新增 XxxRuntime 类（继承 BaseProductRuntime）
//   3. 在 register.ts 中 import + manager.registerRuntime()
// ============================================

import { RuntimeName } from '../../types/runtime';
import { BaseProductRuntime } from './BaseProductRuntime';
import type { ProductCatalogInfo } from './types';

// ============================================
// Hotel Runtime - 酒店运营 SaaS
// ============================================
export class HotelRuntime extends BaseProductRuntime {
  readonly name = RuntimeName.Hotel;
  protected version = '1.0.0';

  protected buildCatalog(): ProductCatalogInfo {
    return {
      productId: 'hotel',
      displayName: 'Hotel SaaS',
      subtitle: '酒店运营 Runtime',
      description:
        '酒店日报自动生成 / 入住率分析 / 收入预测 / 运营建议。Hermes 可直接调用：生成日报 → 分析入住率 → 预测收入 → 给出运营建议 → CRM 收费 → 收入统计。',
      category: 'saas',
      runtimeStatus: 'running',
      owner: 'Linda',
      expectedRevenue: 4800000,
      progress: 68,
      riskLevel: 'low',
      capabilities: ['日报生成', '入住率分析', '收入预测', '运营建议', '多酒店支持'],
      launchUrl: '/cockpit/hotel',
      vaultPath: 'XEAOS/Brand/Hotel SaaS.md',
      milestones: [
        { name: 'P0 MVP 上线', dueDate: '2026-06-30', status: 'completed' },
        { name: 'P1 客户成功闭环', dueDate: '2026-08-15', status: 'in_progress' },
        { name: 'V2 多酒店支持', dueDate: '2026-10-01', status: 'pending' },
      ],
    };
  }
}

// ============================================
// Gold Factory Runtime - 黄金工厂
// ============================================
export class GoldFactoryRuntime extends BaseProductRuntime {
  readonly name = RuntimeName.GoldFactory;
  protected version = '1.0.0';

  protected buildCatalog(): ProductCatalogInfo {
    return {
      productId: 'gold_factory',
      displayName: 'Gold Factory',
      subtitle: '黄金工厂 Runtime',
      description:
        '工厂建模 / MES 集成 / 订单管理。Hermes 可直接调用：生产建模 → MES 集成 → 订单管理 → 客户首单 → 收入回款。',
      category: 'factory',
      runtimeStatus: 'running',
      owner: 'Mike',
      expectedRevenue: 3200000,
      progress: 42,
      riskLevel: 'medium',
      capabilities: ['工厂建模', 'MES 集成', '订单管理', '生产排程', '客户首单'],
      launchUrl: '/cockpit/gold-factory',
      vaultPath: 'XEAOS/Brand/Gold Factory.md',
      milestones: [
        { name: '工厂建模', dueDate: '2026-07-10', status: 'completed' },
        { name: 'MES 集成', dueDate: '2026-08-20', status: 'in_progress' },
        { name: '客户首单', dueDate: '2026-09-15', status: 'pending' },
      ],
    };
  }
}

// ============================================
// AI Destiny Runtime - 国学命理
// ============================================
export class AIDestinyRuntime extends BaseProductRuntime {
  readonly name = RuntimeName.AIDestiny;
  protected version = '1.0.0';

  protected buildCatalog(): ProductCatalogInfo {
    return {
      productId: 'ai_destiny',
      displayName: 'AI Destiny',
      subtitle: '国学命理 Runtime',
      description:
        '八字 / 紫微 / 奇门 / 姓名 / 择日 / 流年。Hermes 可直接调用：八字 → 紫微 → 奇门 → 姓名 → 择日 → 流年 → CRM 收费 → 收入统计。',
      category: 'knowledge',
      runtimeStatus: 'beta',
      owner: 'Alex',
      expectedRevenue: 1800000,
      progress: 25,
      riskLevel: 'high',
      capabilities: ['八字排盘', '紫微斗数', '奇门遁甲', '姓名学', '择日', '流年运势'],
      launchUrl: 'https://xuanji.xshetech.com', // 国学命理网站二级域名（xuanji.xshetech.com 玄机命理）
      vaultPath: 'XEAOS/Project/AI-Destiny.md',
      milestones: [
        { name: '命理数据集构建', dueDate: '2026-07-25', status: 'in_progress' },
        { name: 'MVP 上线', dueDate: '2026-09-30', status: 'pending' },
      ],
    };
  }
}

// ============================================
// Marketing OS Runtime - 营销运营
// ============================================
export class MarketingOSRuntime extends BaseProductRuntime {
  readonly name = RuntimeName.MarketingOS;
  protected version = '1.0.0';

  protected buildCatalog(): ProductCatalogInfo {
    return {
      productId: 'marketing_os',
      displayName: 'Marketing OS',
      subtitle: '营销运营 Runtime',
      description:
        '多平台账号矩阵 / 数据回流 / 营销自动化。Hermes 可直接调用：账号矩阵 → 数据回流 → 内容生产 → 多平台发布 → 营销自动化 → Lead → CRM → 收入。',
      category: 'marketing',
      runtimeStatus: 'paused',
      owner: 'Zoe',
      expectedRevenue: 2400000,
      progress: 35,
      riskLevel: 'medium',
      capabilities: ['多平台账号矩阵', '数据回流', '内容生产', '多平台发布', '营销自动化'],
      launchUrl: '/cockpit/marketing-os',
      vaultPath: 'XEAOS/Brand/Marketing OS.md',
      milestones: [
        { name: '多平台账号矩阵', dueDate: '2026-06-15', status: 'completed' },
        { name: '数据回流打通', dueDate: '2026-08-01', status: 'in_progress' },
      ],
    };
  }
}

// ============================================
// 导出汇总
// ============================================
export { BaseProductRuntime } from './BaseProductRuntime';
export type {
  ProductCatalogInfo,
  ProductQuery,
  ProductCommand,
  ProductLaunchHandle,
  ProductResult,
} from './types';

/**
 * 所有产品 Runtime 类的列表
 *
 * 用于 register.ts 批量注册，也用于其他地方需要遍历产品 Runtime 时使用。
 */
export const PRODUCT_RUNTIME_CLASSES = [
  HotelRuntime,
  GoldFactoryRuntime,
  AIDestinyRuntime,
  MarketingOSRuntime,
] as const;

/**
 * 所有产品 Runtime 的 RuntimeName 列表
 *
 * 用于 ProjectsWorkspace 查询 Catalog 时遍历。
 */
export const PRODUCT_RUNTIME_NAMES: RuntimeName[] = [
  RuntimeName.Hotel,
  RuntimeName.GoldFactory,
  RuntimeName.AIDestiny,
  RuntimeName.MarketingOS,
];
